import { useCallback, useEffect, useState } from "react";
import { onSwChange, swFailure } from "./swRegistry.ts";

export type SwState =
  /** No service worker API — plain http:// origin, or an old browser. */
  | "unsupported"
  /** Supported, but nothing registered yet. */
  | "off"
  /** First install in progress; the offline copy is not complete. */
  | "installing"
  /** Registered and serving. */
  | "active"
  /** A newer worker is installed and waiting for the page to reload. */
  | "waiting"
  /** Registration was refused — offline support is not going to happen. */
  | "failed";

export interface SwStatus {
  state: SwState;
  /** Media files kept for offline use. */
  cachedMedia: number;
  /** Bytes this origin is using, per the storage estimate. */
  usedBytes: number | null;
  refresh: () => void;
}

/**
 * What the offline layer is actually doing.
 *
 * Worth surfacing here because this app's offline story is unusual: the shell
 * is cached on install, but the ~400 MB of question media is only kept as you
 * see it. "How much have I got offline?" is a real question with a real
 * answer, and nothing in the browser UI tells you.
 */
export function useServiceWorker(): SwStatus {
  const [state, setState] = useState<SwState>(
    typeof navigator === "undefined" || !("serviceWorker" in navigator)
      ? "unsupported"
      : "off"
  );
  const [cachedMedia, setCachedMedia] = useState(0);
  const [usedBytes, setUsedBytes] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      setState("unsupported");
      return;
    }

    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg && swFailure()) setState("failed");
    else if (!reg) setState("off");
    else if (reg.waiting) setState("waiting");
    else if (reg.active) setState("active");
    else if (reg.installing) setState("installing");
    else setState("off");

    try {
      const keys = await caches.keys();
      const media = keys.filter((k) => k.startsWith("t24-media"));
      let count = 0;
      for (const name of media) count += (await (await caches.open(name)).keys()).length;
      setCachedMedia(count);
    } catch {
      // Storage blocked (private browsing); leave the count as it was.
    }

    try {
      const estimate = await navigator.storage?.estimate?.();
      setUsedBytes(typeof estimate?.usage === "number" ? estimate.usage : null);
    } catch {
      setUsedBytes(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    // Registration finishing, and a new worker taking over, both change what
    // this reports.
    const onChange = () => void refresh();
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    const stopWatchingRegistration = onSwChange(onChange);
    let cancelled = false;
    void navigator.serviceWorker.ready.then(() => {
      if (!cancelled) void refresh();
    });
    return () => {
      cancelled = true;
      stopWatchingRegistration();
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
    };
  }, [refresh]);

  return { state, cachedMedia, usedBytes, refresh };
}

/** Bytes as MB/GB, for a one-line status. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  const mb = bytes / (1024 * 1024);
  return mb < 1024 ? `${Math.round(mb)} MB` : `${(mb / 1024).toFixed(1)} GB`;
}
